/**
 * The browser's own validation bubble, in the language of the surface it
 * appears on.
 *
 * A `required` control that is submitted empty is refused by the browser, not
 * by this console — and the browser writes that refusal in **its own** UI
 * locale. On a machine set to English it read «Please fill out this field.» in
 * a Latin bubble beside an Arabic label on an Arabic-only, right-to-left
 * console. Nothing in the page could be blamed for it and nothing in the page
 * could be read to recover from it.
 *
 * `setCustomValidity` is the one lever that replaces that text. Two rules make
 * it behave:
 *
 * - **Set it only while the control is invalid.** A non-empty custom message
 *   *is* an invalid state, so a message left behind after the reader types
 *   makes a filled field refuse to submit. Clearing it on every input is what
 *   keeps the field usable.
 * - **Set it against the reason, not against the event.** A field can be empty,
 *   or filled with something the pattern refuses; one sentence for both would
 *   tell a reader who typed a bad phone number to fill in a field they can see
 *   they have filled.
 *
 * The visible, in-page error a surface renders for itself is unaffected — this
 * only replaces the text the browser was going to draw anyway.
 */

/** What each refusal says, in the vocabulary the auth pack already uses. */
const MESSAGES = {
  empty: 'هذا الحقل مطلوب.',
  mismatch: 'الصيغة غير مقبولة. راجع ما كتبته.',
} as const;

type Validatable = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;

/** The sentence a control's current invalidity deserves, or `''` when it is valid. */
export function nativeValidityMessage(control: Validatable): string {
  const state = control.validity;
  if (state.valueMissing) return MESSAGES.empty;
  if (state.customError) return '';
  if (state.valid) return '';

  return MESSAGES.mismatch;
}

/**
 * Re-state one control's native refusal in Arabic.
 *
 * Called from the control's own `invalid` and `input` handlers: the first
 * replaces the bubble before the browser draws it, the second clears the custom
 * message so the field stops refusing once it has been corrected.
 */
export function applyNativeValidity(control: Validatable): void {
  // Clear first: `validity` is computed against the custom message too, so a
  // stale one would make every field report `customError` forever.
  control.setCustomValidity('');
  control.setCustomValidity(nativeValidityMessage(control));
}
